import React from "react";
import { Button, IconButton, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import LinkedInIcon from "@material-ui/icons/LinkedIn";
import TelegramIcon from "@material-ui/icons/Telegram";
import EmailOutlinedIcon from "@material-ui/icons/EmailOutlined";
import GetAppOutlinedIcon from "@material-ui/icons/GetAppOutlined";
import ArrowForwardRoundedIcon from "@material-ui/icons/ArrowForwardRounded";
import MemoryOutlinedIcon from "@material-ui/icons/MemoryOutlined";
import SearchOutlinedIcon from "@material-ui/icons/SearchOutlined";
import CodeOutlinedIcon from "@material-ui/icons/CodeOutlined";

import { Link } from "react-router-dom";
import { getTranslate } from "../localization";

import "./home.css";

const useStyles = makeStyles((theme) => ({
  root: {
    position: "relative",
    minHeight: "100vh",
    width: "100%",
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
    direction: theme.direction,
    background: "#070d18",
    [theme.breakpoints.down("sm")]: {
      minHeight: "calc(100vh - 64px)",
    },
  },
  shell: {
    position: "relative",
    zIndex: 3,
    width: "min(100%, 1320px)",
    margin: "0 auto",
    padding: "72px 48px",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.15fr) minmax(320px, .85fr)",
    gap: 58,
    alignItems: "center",
    direction: theme.direction,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
      gap: 34,
      padding: "58px 34px",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "42px 18px 50px",
      gap: 28,
    },
  },
  intro: {
    minWidth: 0,
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
  kicker: {
    display: "inline-flex",
    alignItems: "center",
    maxWidth: "100%",
    padding: "8px 14px",
    marginBottom: 22,
    borderRadius: 999,
    color: "#8dc4ff",
    backgroundColor: "rgba(22,139,255,.07)",
    border: "1px solid rgba(22,139,255,.20)",
    fontSize: 10,
    fontWeight: 800,
    lineHeight: 1.5,
    letterSpacing: theme.direction === "rtl" ? 0 : ".11em",
    textTransform: theme.direction === "rtl" ? "none" : "uppercase",
  },
  title: {
    margin: 0,
    color: "#f8fafc",
    fontWeight: 850,
    fontSize: "clamp(2.7rem, 5vw, 5rem)",
    lineHeight: 1.06,
    letterSpacing: theme.direction === "rtl" ? 0 : "-.045em",
    textAlign: theme.direction === "rtl" ? "right" : "left",
    overflowWrap: "break-word",
    [theme.breakpoints.down("xs")]: {
      fontSize: "clamp(2.35rem, 12vw, 3.4rem)",
      lineHeight: 1.2,
    },
  },
  name: {
    color: "#2092ff",
  },
  headline: {
    maxWidth: 780,
    marginTop: 24,
    color: "#dfe7f1",
    fontSize: "clamp(1.05rem, 1.8vw, 1.45rem)",
    lineHeight: 1.7,
    fontWeight: 700,
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
  desc: {
    maxWidth: 800,
    marginTop: 16,
    color: "#93a1b6",
    fontSize: 14,
    lineHeight: 2,
    textAlign: theme.direction === "rtl" ? "right" : "left",
    [theme.breakpoints.down("xs")]: {
      fontSize: 13,
      lineHeight: 1.95,
    },
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 30,
    justifyContent: "flex-start",
    [theme.breakpoints.down("xs")]: {
      alignItems: "stretch",
      flexDirection: "column",
    },
  },
  primaryButton: {
    minHeight: 48,
    padding: "9px 20px",
    borderRadius: 12,
    color: "#fff",
    fontSize: 11,
    fontWeight: 800,
    background: "linear-gradient(135deg,#168bff,#2fa0ff)",
    boxShadow: "0 14px 34px rgba(22,139,255,.21)",
    transition: "transform .2s ease, box-shadow .2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 18px 40px rgba(22,139,255,.28)",
      background: "linear-gradient(135deg,#2495ff,#55b3ff)",
    },
  },
  secondaryButton: {
    minHeight: 48,
    padding: "9px 20px",
    borderRadius: 12,
    color: "#cdd8e7",
    fontSize: 11,
    fontWeight: 800,
    background: "rgba(255,255,255,.025)",
    border: "1px solid rgba(148,163,184,.17)",
    transition: "transform .2s ease, color .2s ease, border-color .2s ease, background-color .2s ease",
    "&:hover": {
      color: "#fff",
      transform: "translateY(-2px)",
      borderColor: "rgba(22,139,255,.36)",
      backgroundColor: "rgba(22,139,255,.06)",
    },
  },
  buttonInner: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    direction: theme.direction,
    gap: 10,
    width: "100%",
  },
  directionalIcon: {
    transform: theme.direction === "rtl" ? "scaleX(-1)" : "none",
  },
  social: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 25,
    justifyContent: "flex-start",
  },
  socialButton: {
    width: 40,
    height: 40,
    color: "#7f8da3",
    border: "1px solid rgba(148,163,184,.14)",
    background: "rgba(255,255,255,.018)",
    "&:hover": {
      color: "#fff",
      transform: "translateY(-2px)",
      borderColor: "rgba(22,139,255,.40)",
      backgroundColor: "rgba(22,139,255,.07)",
    },
  },
  proofPanel: {
    position: "relative",
    minWidth: 0,
    padding: 24,
    borderRadius: 24,
    overflow: "hidden",
    background: "linear-gradient(145deg,rgba(14,26,49,.90),rgba(7,16,30,.93))",
    border: "1px solid rgba(100,123,168,.16)",
    boxShadow: "0 24px 60px rgba(0,0,0,.23)",
    backdropFilter: "blur(12px)",
    "&::before": {
      content: '""',
      position: "absolute",
      width: 250,
      height: 250,
      borderRadius: "50%",
      top: -140,
      right: theme.direction === "rtl" ? "auto" : -110,
      left: theme.direction === "rtl" ? -110 : "auto",
      background: "radial-gradient(circle,rgba(22,139,255,.14),transparent 68%)",
      pointerEvents: "none",
    },
    [theme.breakpoints.down("xs")]: {
      padding: 18,
      borderRadius: 20,
    },
  },
  proofTitle: {
    color: "#f5f8fc",
    fontSize: 16,
    fontWeight: 800,
    marginBottom: 7,
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
  proofLead: {
    color: "#7f8da4",
    fontSize: 12,
    lineHeight: 1.8,
    marginBottom: 18,
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
  proofList: {
    display: "grid",
    gap: 10,
  },
  proofItem: {
    display: "grid",
    gridTemplateColumns: "42px minmax(0,1fr)",
    gap: 12,
    alignItems: "start",
    direction: theme.direction,
    padding: 14,
    borderRadius: 15,
    background: "rgba(255,255,255,.022)",
    border: "1px solid rgba(148,163,184,.09)",
  },
  proofIcon: {
    width: 42,
    height: 42,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 12,
    color: "#35a0ff",
    background: "rgba(22,139,255,.075)",
    border: "1px solid rgba(22,139,255,.14)",
  },
  proofText: {
    minWidth: 0,
    color: "#a3b0c2",
    fontSize: 11.5,
    lineHeight: 1.78,
    textAlign: theme.direction === "rtl" ? "right" : "left",
    overflowWrap: "break-word",
  },
}));

export default function HomeScreen() {
  const classes = useStyles();
  const translate = getTranslate();

  const proofIcons = [
    <MemoryOutlinedIcon fontSize="small" />,
    <SearchOutlinedIcon fontSize="small" />,
    <CodeOutlinedIcon fontSize="small" />,
  ];

  return (
    <section className={classes.root}>
      <div className="ai-background" aria-hidden="true">
        <div className="ai-grid" />
        <div className="ai-glow ai-glow-one" />
        <div className="ai-glow ai-glow-two" />
        <div className="ai-orbit" />
        <span className="ai-node ai-node-1" />
        <span className="ai-node ai-node-2" />
        <span className="ai-node ai-node-3" />
        <span className="ai-node ai-node-4" />
        <span className="ai-node ai-node-5" />
        <span className="ai-node ai-node-6" />
        <span className="ai-line ai-line-1" />
        <span className="ai-line ai-line-2" />
        <span className="ai-line ai-line-3" />
        <span className="ai-line ai-line-4" />
      </div>

      <div className={classes.shell}>
        <div className={classes.intro}>
          <div className={classes.kicker}>{translate.homeKicker}</div>

          <Typography component="h1" className={classes.title}>
            {translate.hi}
            <span className={classes.name}>{translate.name}</span>
            {translate.hi2}
          </Typography>

          <Typography component="h2" className={classes.headline}>
            {translate.homeHeadline}
          </Typography>

          <Typography component="p" className={classes.desc}>
            {translate.desc}
          </Typography>

          <div className={classes.actions}>
            <Button component={Link} to="/portfolio" className={classes.primaryButton}>
              <span className={classes.buttonInner}>
                <span>{translate.viewProjects}</span>
                <ArrowForwardRoundedIcon className={classes.directionalIcon} fontSize="small" />
              </span>
            </Button>

            <Button
              component="a"
              href="/Mehdi_Talebi_Resume.pdf"
              download
              className={classes.secondaryButton}
            >
              <span className={classes.buttonInner}>
                <span>{translate.downloadcv}</span>
                <GetAppOutlinedIcon fontSize="small" />
              </span>
            </Button>
          </div>

          <div className={classes.social}>
            <a href="https://www.linkedin.com/in/mehditalebi-/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <IconButton className={classes.socialButton}><LinkedInIcon fontSize="small" /></IconButton>
            </a>
            <a href="https://t.me/Mehdita" target="_blank" rel="noopener noreferrer" aria-label="Telegram">
              <IconButton className={classes.socialButton}><TelegramIcon fontSize="small" /></IconButton>
            </a>
            <a href="mailto:mehditalebi9675@gmail.com" aria-label="Email">
              <IconButton className={classes.socialButton}><EmailOutlinedIcon fontSize="small" /></IconButton>
            </a>
          </div>
        </div>

        <aside className={classes.proofPanel}>
          <Typography className={classes.proofTitle}>{translate.homeProofTitle}</Typography>
          <div className={classes.proofLead}>{translate.homeProofLead}</div>

          <div className={classes.proofList}>
            {translate.homeSignals.map((item, index) => (
              <div className={classes.proofItem} key={item}>
                <div className={classes.proofIcon}>{proofIcons[index]}</div>
                <div className={classes.proofText}>{item}</div>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </section>
  );
}
